import React from 'react';
import { Button, Modal } from 'antd';
import type { SeleccionPerfil } from '../../api/feriasApi';
import { FctAviso, FctEstado } from '../../fct/react';
import { etiquetas, PLAZOS, PROVINCIAS, TRAMOS } from './clienteCatalogos';
import { FILAS } from './clienteFilas';
import type { Cambio, Valores } from './clienteFilas';

/** Lo que hará Guardar: actualizar el cliente cargado, o crear uno nuevo. */
export type Accion =
  | {
      tipo: 'actualizar';
      codigo: string;
      nombre: string;
      cambios: Cambio[];
      /** Los campos vacíos en la columna de Gescom que Gescom tiene: se quedan. */
      vacios: string[];
      /** El perfil final, cuando difiere del de Gescom (D32). */
      perfil: SeleccionPerfil | null;
      /** Guardar un perfil reactiva a un inversor dado de baja. */
      reactiva: boolean;
      /** El cliente del caso hasta ahora, cuando es otro: el caso pasa a este (D39). */
      anterior: string | null;
    }
  | {
      tipo: 'crear';
      valores: Valores;
      perfil: SeleccionPerfil;
      /** Lo que aún falta del mínimo de D37, como lo nombra la pantalla. */
      faltan: string[];
      /** El cliente del caso hasta ahora, si tiene: el caso pasa al cliente nuevo (D39). */
      anterior: string | null;
    };

interface Props {
  accion: Accion | null;
  guardando: boolean;
  /** Por qué la API rechazó el guardado. */
  error: string | null;
  onConfirmar: () => void;
  onCancelar: () => void;
}

// El perfil de compra en palabras.
function textoPerfil(p: SeleccionPerfil): string {
  return [`plazo ${p.plazo ? PLAZOS[p.plazo] : 'sin marcar'}`, `tramos ${etiquetas(TRAMOS, p.tramos)}`, `provincias ${etiquetas(PROVINCIAS, p.provincias)}`].join(
    '; ',
  );
}

const etiqueta = (clave: string) => FILAS.find((f) => f.clave === clave)?.etiqueta ?? clave;

/**
 * La confirmación antes de guardar (#98 de fct-ferias): dice la acción con su propia marca (icono, color y
 * título), lo que cambiará y, para un cliente nuevo, lo que aún falta del mínimo de D37, que impide guardar.
 */
export function ConfirmarGuardado({ accion, guardando, error, onConfirmar, onCancelar }: Props) {
  if (!accion) {
    return null;
  }
  const actualiza = accion.tipo === 'actualizar';
  const bloqueado = accion.tipo === 'crear' && accion.faltan.length > 0;

  return (
    <Modal
      open
      title={actualiza ? `Se actualizará el cliente ${accion.codigo || 'sin código'}` : 'Se creará el nuevo cliente'}
      onCancel={onCancelar}
      footer={
        <>
          <Button onClick={onCancelar} disabled={guardando}>
            Cancelar
          </Button>
          <Button type="primary" onClick={onConfirmar} loading={guardando} disabled={bloqueado}>
            {actualiza ? 'Actualizar cliente' : 'Crear cliente'}
          </Button>
        </>
      }
    >
      <div role="note" aria-label="Acción">
        <FctEstado
          personalizado={
            actualiza
              ? { etiqueta: 'Actualizar cliente', familia: 'proceso', icono: 'editar' }
              : { etiqueta: 'Cliente nuevo', familia: 'aviso', icono: 'mas' }
          }
        />
      </div>

      {accion.anterior && (
        <FctAviso tipo="aviso" titulo="El caso cambiará de cliente">
          El caso pasará del cliente {accion.anterior} al{' '}
          {accion.tipo === 'actualizar'
            ? `cliente ${accion.codigo || 'sin código'} · ${accion.nombre}`
            : `nuevo cliente ${[accion.valores.nombre, accion.valores.apellido1]
                .map((v) => (v ?? '').trim())
                .filter((v) => v !== '')
                .join(' ')}`}
          . El cliente anterior no cambia.
        </FctAviso>
      )}

      {accion.tipo === 'actualizar' ? (
        <>
          <p>{accion.nombre}</p>
          {accion.cambios.length === 0 && !accion.perfil ? (
            <p>No hay datos que cambiar: el caso quedará enlazado a este cliente.</p>
          ) : (
            <ul aria-label="Cambios">
              {accion.cambios.map((c) => (
                <li key={c.clave}>
                  {c.clave === 'movil' && c.antes
                    ? `Móvil: se sustituirá ${c.antes} por ${c.despues}`
                    : `${etiqueta(c.clave)}: ${c.antes || 'vacío'} → ${c.despues}`}
                </li>
              ))}
              {accion.perfil && <li>Perfil de compra: {textoPerfil(accion.perfil)}</li>}
            </ul>
          )}
          {accion.vacios.length > 0 && <p>Un campo vacío no borra nada: {accion.vacios.join(', ')} se quedan como están en Gescom.</p>}
          {accion.reactiva && (
            <FctAviso tipo="aviso" titulo="Este inversor está dado de baja">
              Guardar el perfil de compra lo vuelve a dar de alta en Gescom.
            </FctAviso>
          )}
        </>
      ) : (
        <>
          <ul aria-label="Datos del cliente nuevo">
            {FILAS.filter((f) => accion.valores[f.clave]?.trim()).map((f) => (
              <li key={f.clave}>
                {f.etiqueta}: {accion.valores[f.clave].trim()}
              </li>
            ))}
            <li>Perfil de compra: {textoPerfil(accion.perfil)}</li>
          </ul>
          <p>Su código será CL seguido del número que Gescom le asigne, y se conocerá al guardarlo.</p>
          {accion.faltan.length > 0 && (
            <FctAviso tipo="error" titulo="Faltan datos para crear el cliente">
              Hacen falta: {accion.faltan.join(', ')}. Complételos en la columna de Gescom.
            </FctAviso>
          )}
        </>
      )}

      {error && (
        <FctAviso tipo="error" titulo="No se ha guardado">
          {error}
        </FctAviso>
      )}
    </Modal>
  );
}
